import React from 'react';

import { GeneralError } from '~src/commons/components/general-error';
import { useAriaTitle } from '~src/commons/utils/hooks/a11y-hooks';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { FullscreenMode } from '~src/fullscreen-internals/fullscreen-mode';
import { useGlobalState } from '~src/global-state';

import { usePurchaseCountryMutation } from './country-mutation';
import { useCountryNamesLocalizationQuery } from './country-names';
import { CountrySelectDropdown } from './country-select-dropdown';
import { CountrySelectModal } from './country-select-modal';

const CountrySelect: React.FC = () => {
  const globalState = useGlobalState();
  const { tICU } = useTranslations();

  const [modalOpen, setModalOpen] = React.useState<boolean>(false);

  const { data: countryNamesLocalization } = useCountryNamesLocalizationQuery();
  const countryMutation = usePurchaseCountryMutation();

  useAriaTitle(tICU('widget.aria.COUNTRY_SELECT_MODAL.TITLE'), 'fullscreen', modalOpen);

  if (!countryNamesLocalization) {
    return null;
  }

  const renderModal = () => {
    if (!modalOpen) {
      return null;
    }
    return (
      <FullscreenMode>
        <CountrySelectModal
          countryNames={countryNamesLocalization.translations}
          onClose={() => setModalOpen(false)}
          onSubmit={(newCountry: string) => {
            setModalOpen(false);
            countryMutation.mutate(newCountry);
          }}
        />
      </FullscreenMode>
    );
  };

  return (
    <>
      <CountrySelectDropdown open={modalOpen} onClick={() => setModalOpen(true)}>
        {countryNamesLocalization.translations[`country.${globalState.purchase_country}`]}
      </CountrySelectDropdown>

      {renderModal()}

      <GeneralError error={countryMutation.error} onClose={countryMutation.reset} />
    </>
  );
};

export const CountrySelectProxy: React.FC = () => {
  const globalState = useGlobalState();

  if (globalState.sessions[0].flags.enable_country_selector) {
    return <CountrySelect />;
  }

  return null;
};
